// POBManager.js
import React, { useState, useEffect, useCallback } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { DataTable } from '../components/tables';
import { apiFetch } from '../api/api';
import { formatDisplayValue } from '../utils/formatUtils';
import './POBManager.css';

// Personnel Form Component
const PersonnelForm = ({ formData, setFormData, onSubmit, onCancel, columns = [], isEditing = false }) => {
  const handleInputChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit(formData);
  };

  return (
    <form onSubmit={handleSubmit} className="personnel-form">
      <h3>{isEditing ? 'Edit Personnel' : 'Add New Personnel'}</h3>
      
      <div className="form-grid">
        {columns.map(col => (
          <div key={col.name} className="form-group">
            <label htmlFor={col.name}>
              {col.label || col.name}
              {col.unit && ` (${col.unit})`}
            </label>
            
            {col.type === 'email' ? (
              <input
                type="email"
                id={col.name}
                value={formData[col.name] || ''}
                onChange={(e) => handleInputChange(col.name, e.target.value)}
                placeholder={`Enter ${col.label || col.name}`}
                className="form-input"
              />
            ) : col.type === 'phone' ? (
              <input
                type="tel"
                id={col.name}
                value={formData[col.name] || ''}
                onChange={(e) => handleInputChange(col.name, e.target.value)}
                placeholder={`Enter ${col.label || col.name}`}
                className="form-input"
              />
            ) : (
              <input
                type="text"
                id={col.name}
                value={formData[col.name] || ''}
                onChange={(e) => handleInputChange(col.name, e.target.value)}
                placeholder={`Enter ${col.label || col.name}`}
                className="form-input"
              />
            )}
            
            {col.desc && (
              <small className="field-description">{col.desc}</small>
            )}
          </div>
        ))}
      </div>
      
      <div className="form-actions">
        <button type="submit" className="btn btn-primary">
          {isEditing ? 'Update Personnel' : 'Add Personnel'}
        </button>
        <button type="button" onClick={onCancel} className="btn btn-secondary">
          Cancel
        </button>
      </div>
    </form>
  );
};

// Personnel Assignment Form Component
const AssignmentForm = ({ personnelList, onAssign, onCancel, reportId }) => {
  const [selectedPersonnel, setSelectedPersonnel] = useState('');
  const [arrival, setArrival] = useState('');
  const [departure, setDeparture] = useState('');

  const handleSubmit = (e) => {
    e.preventDefault();
    if (!selectedPersonnel) return;

    onAssign({
      POBID: parseInt(selectedPersonnel),
      ReportID: reportId,
      Arrival: arrival || null,
      Departure: departure || null
    });
  };

  return (
    <form onSubmit={handleSubmit} className="assignment-form">
      <h3>Assign Personnel to Report</h3>
      
      <div className="form-grid">
        <div className="form-group">
          <label htmlFor="personnel">Select Personnel</label>
          <select
            id="personnel"
            value={selectedPersonnel}
            onChange={(e) => setSelectedPersonnel(e.target.value)}
            className="form-input"
            required
          >
            <option value="">-- Select Personnel --</option>
            {personnelList.map(person => (
              <option key={person.POBID} value={person.POBID}>
                {person.Name} - {person.Position} ({person.Company})
              </option>
            ))}
          </select>
        </div>
        
        <div className="form-group">
          <label htmlFor="arrival">Arrival Date/Time</label>
          <input
            type="datetime-local"
            id="arrival"
            value={arrival}
            onChange={(e) => setArrival(e.target.value)}
            className="form-input"
          />
        </div>
        
        <div className="form-group">
          <label htmlFor="departure">Departure Date/Time</label>
          <input
            type="datetime-local"
            id="departure"
            value={departure}
            onChange={(e) => setDeparture(e.target.value)}
            className="form-input"
          />
        </div>
      </div>
      
      <div className="form-actions">
        <button type="submit" className="btn btn-primary">
          Assign to Report
        </button>
        <button type="button" onClick={onCancel} className="btn btn-secondary">
          Cancel
        </button>
      </div>
    </form>
  );
};

export default function POBManager({ reportId: propReportId, onDataChange, readOnly = false }) {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  
  // Get reportId from props or URL params
  const urlReportId = searchParams.get('reportId');
  const reportId = propReportId || urlReportId;
  
  // State management
  const [personnelList, setPersonnelList] = useState([]);
  const [reportAssignments, setReportAssignments] = useState([]);
  const [personnelColumns, setPersonnelColumns] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [activeTab, setActiveTab] = useState(reportId ? 'assignments' : 'personnel');
  
  // Form states
  const [showPersonnelForm, setShowPersonnelForm] = useState(false);
  const [showAssignmentForm, setShowAssignmentForm] = useState(false);
  const [editingPersonnel, setEditingPersonnel] = useState(null);
  const [personnelFormData, setPersonnelFormData] = useState({});
  const [isLoadingConfig, setIsLoadingConfig] = useState(true);
  
  // User permissions
  const role = localStorage.getItem('role');
  const token = localStorage.getItem('token');

  // Fetch personnel column configuration
  const fetchPersonnelConfig = useCallback(async () => {
    setIsLoadingConfig(true);
    try {
      const allConfigs = await apiFetch('/api/config/columns');
      const personnelConfig = Array.isArray(allConfigs) 
        ? allConfigs.find(config => config.tableName === "POB") 
        : null;

      if (personnelConfig && Array.isArray(personnelConfig.columns)) {
        // Filter columns relevant for personnel management
        const relevantColumns = personnelConfig.columns.filter(col => 
          ['Name', 'Position', 'Company', 'Email', 'Phone'].includes(col.name)
        );
        
        const sortedColumns = relevantColumns.sort((a, b) => a.order - b.order);
        setPersonnelColumns(sortedColumns);
        
        // Initialize empty form data
        const emptyData = sortedColumns.reduce((obj, col) => ({ ...obj, [col.name]: '' }), {});
        setPersonnelFormData(emptyData);
      } else {
        console.error("Personnel configuration not found");
        setError('Personnel configuration not found. Please check admin configuration.');
      }
    } catch (err) {
      console.error('Failed to fetch personnel config:', err);
      setError('Failed to load personnel configuration: ' + (err.message || 'Unknown error'));
    }
    setIsLoadingConfig(false);
  }, []);

  // Fetch personnel list
  const fetchPersonnelList = useCallback(async () => {
    try {
      const data = await apiFetch('/api/pob/personnel');
      setPersonnelList(Array.isArray(data) ? data : []);
    } catch (err) {
      console.error('Failed to fetch personnel list:', err);
      setError('Failed to load personnel list: ' + (err.message || 'Unknown error'));
    }
  }, []);

  // Fetch report assignments
  const fetchReportAssignments = useCallback(async () => {
    if (!reportId) return;
    
    try {
      const data = await apiFetch(`/api/pob/assignments/${reportId}`);
      const assignments = Array.isArray(data) ? data : [];
      setReportAssignments(assignments);
      
      // Notify parent component of data change if callback provided
      if (onDataChange) {
        onDataChange(assignments);
      }
    } catch (err) {
      console.error('Failed to fetch report assignments:', err);
      setError('Failed to load report assignments: ' + (err.message || 'Unknown error'));
      setReportAssignments([]);
    }
  }, [reportId, onDataChange]);

  // Effects
  useEffect(() => {
    if (!token) {
      navigate('/login');
      return;
    }
    fetchPersonnelConfig();
  }, [token, navigate, fetchPersonnelConfig]);

  useEffect(() => {
    if (!isLoadingConfig) {
      setLoading(true);
      Promise.all([
        fetchPersonnelList(),
        reportId ? fetchReportAssignments() : Promise.resolve()
      ]).finally(() => setLoading(false));
    }
  }, [isLoadingConfig, fetchPersonnelList, fetchReportAssignments, reportId]);

  // Personnel CRUD operations
  const handlePersonnelSubmit = async (data) => {
    try {
      if (editingPersonnel) {
        // Update existing personnel
        await apiFetch(`/api/pob/personnel/${editingPersonnel.POBID}`, {
          method: 'PUT',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data)
        });
      } else {
        // Add new personnel
        await apiFetch('/api/pob/personnel', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify(data)
        });
      }

      // Refresh personnel list and close form
      await fetchPersonnelList();
      handleCancelPersonnelForm();
      
    } catch (err) {
      console.error('Failed to save personnel:', err);
      setError('Failed to save personnel: ' + (err.message || 'Unknown error'));
    }
  };

  const handlePersonnelEdit = (personnel) => {
    setEditingPersonnel(personnel);
    
    // Pre-populate form with existing data
    const editData = personnelColumns.reduce((obj, col) => {
      return { ...obj, [col.name]: personnel[col.name] || '' };
    }, {});
    
    setPersonnelFormData(editData);
    setShowPersonnelForm(true);
  };

  const handlePersonnelDelete = async (personnel) => {
    if (!window.confirm(`Are you sure you want to remove ${personnel.Name} from the personnel list?`)) {
      return;
    }

    try {
      await apiFetch(`/api/pob/personnel/${personnel.POBID}`, {
        method: 'DELETE'
      });
      await fetchPersonnelList();
    } catch (err) {
      console.error('Failed to delete personnel:', err);
      setError('Failed to delete personnel: ' + (err.message || 'Unknown error'));
    }
  };

  const handleAddPersonnel = () => {
    setEditingPersonnel(null);
    const emptyData = personnelColumns.reduce((obj, col) => ({ ...obj, [col.name]: '' }), {});
    setPersonnelFormData(emptyData);
    setShowPersonnelForm(true);
  };

  const handleCancelPersonnelForm = () => {
    setShowPersonnelForm(false);
    setEditingPersonnel(null);
    const emptyData = personnelColumns.reduce((obj, col) => ({ ...obj, [col.name]: '' }), {});
    setPersonnelFormData(emptyData);
  };

  // Assignment operations
  const handleAssignPersonnel = async (assignmentData) => {
    try {
      await apiFetch('/api/pob/assignments', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(assignmentData)
      });

      // Refresh assignments and close form
      await fetchReportAssignments();
      setShowAssignmentForm(false);
      
    } catch (err) {
      console.error('Failed to assign personnel:', err);
      setError('Failed to assign personnel: ' + (err.message || 'Unknown error'));
    }
  };

  const handleRemoveAssignment = async (assignment) => {
    if (!window.confirm(`Are you sure you want to remove ${assignment.Name} from this report?`)) {
      return;
    }

    try {
      await apiFetch(`/api/pob/assignments/${assignment.AssignmentID}`, {
        method: 'DELETE'
      });
      await fetchReportAssignments();
    } catch (err) {
      console.error('Failed to remove assignment:', err);
      setError('Failed to remove assignment: ' + (err.message || 'Unknown error'));
    }
  };

  // Get available personnel (not already assigned to this report)
  const getAvailablePersonnel = () => {
    if (!reportId) return personnelList;
    
    const assignedPersonnelIds = reportAssignments.map(assignment => assignment.POBID);
    return personnelList.filter(person => !assignedPersonnelIds.includes(person.POBID));
  };

  // Render loading state
  if (isLoadingConfig || loading) {
    return <div className="pob-container loading">Loading POB data...</div>;
  }

  // Render error state
  if (error && !personnelColumns.length) {
    return <div className="pob-container error">Error: {error}</div>;
  }

  // Assignment columns for display
  const assignmentColumns = [
    { name: 'Name', label: 'Name', type: 'string', order: 1 },
    { name: 'Position', label: 'Position', type: 'string', order: 2 },
    { name: 'Company', label: 'Company', type: 'string', order: 3 },
    { name: 'Arrival', label: 'Arrival', type: 'datetime', order: 4 },
    { name: 'Departure', label: 'Departure', type: 'datetime', order: 5 }
  ];

  // Main render
  return (
    <div className="pob-container">
      {/* Standalone page header (only show if not embedded) */}
      {!propReportId && (
        <div className="pob-header">
          <h1>Personnel On Board (POB) Manager</h1>
          {reportId && (
            <p className="report-context">Managing assignments for Report ID: {reportId}</p>
          )}
        </div>
      )}

      {/* Error display */}
      {error && (
        <div className="error-message">
          {error}
        </div>
      )}

      {/* Tab Navigation */}
      {!propReportId && (
        <div className="tab-navigation">
          <button 
            className={`tab ${activeTab === 'personnel' ? 'active' : ''}`}
            onClick={() => setActiveTab('personnel')}
          >
            Personnel List ({personnelList.length})
          </button>
          {reportId && (
            <button 
              className={`tab ${activeTab === 'assignments' ? 'active' : ''}`}
              onClick={() => setActiveTab('assignments')}
            >
              Report Assignments ({reportAssignments.length})
            </button>
          )}
        </div>
      )}

      {/* Personnel Management Tab */}
      {(activeTab === 'personnel' && !propReportId) && (
        <>
          {/* Personnel Controls */}
          {!readOnly && (role === 'admin' || role === 'read-write') && !showPersonnelForm && (
            <div className="pob-controls">
              <button onClick={handleAddPersonnel} className="btn btn-primary add-personnel-btn">
                + Add New Personnel
              </button>
            </div>
          )}

          {/* Personnel Form */}
          {showPersonnelForm && !readOnly && (
            <div className="pob-form-container">
              <PersonnelForm
                formData={personnelFormData}
                setFormData={setPersonnelFormData}
                onSubmit={handlePersonnelSubmit}
                onCancel={handleCancelPersonnelForm}
                columns={personnelColumns}
                isEditing={!!editingPersonnel}
              />
            </div>
          )}

          {/* Personnel Table */}
          <div className="pob-table-container">
            {personnelList.length > 0 ? (
              <DataTable
                data={personnelList}
                columns={personnelColumns}
                role={role}
                onEdit={!readOnly ? handlePersonnelEdit : null}
                onDelete={!readOnly && role === 'admin' ? handlePersonnelDelete : null}
                onDataChange={fetchPersonnelList}
                title={`Personnel Database (${personnelList.length} people)`}
                tableName="POB"
                itemsPerPage={10}
              />
            ) : (
              <div className="no-data">
                <p>No personnel in the database.</p>
                {!readOnly && (role === 'admin' || role === 'read-write') && (
                  <button onClick={handleAddPersonnel} className="btn btn-primary">
                    Add First Personnel
                  </button>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* Report Assignments Tab */}
      {(activeTab === 'assignments' || propReportId) && reportId && (
        <>
          {/* Assignment Controls */}
          {!readOnly && (role === 'admin' || role === 'read-write') && !showAssignmentForm && (
            <div className="pob-controls">
              <button 
                onClick={() => setShowAssignmentForm(true)} 
                className="btn btn-primary add-personnel-btn"
                disabled={getAvailablePersonnel().length === 0}
              >
                + Assign Personnel to Report
              </button>
              {getAvailablePersonnel().length === 0 && (
                <span className="help-text">All personnel are already assigned to this report</span>
              )}
            </div>
          )}

          {/* Assignment Form */}
          {showAssignmentForm && !readOnly && (
            <div className="pob-form-container">
              <AssignmentForm
                personnelList={getAvailablePersonnel()}
                onAssign={handleAssignPersonnel}
                onCancel={() => setShowAssignmentForm(false)}
                reportId={reportId}
              />
            </div>
          )}

          {/* Assignments Table */}
          <div className="pob-table-container">
            {reportAssignments.length > 0 ? (
              <DataTable
                data={reportAssignments}
                columns={assignmentColumns}
                role={role}
                onEdit={null} // Assignments are managed through forms
                onDelete={!readOnly && (role === 'admin' || role === 'read-write') ? handleRemoveAssignment : null}
                onDataChange={fetchReportAssignments}
                title={`Report Assignments (${reportAssignments.length} people)`}
                tableName="POBReportAssignment"
                itemsPerPage={10}
              />
            ) : (
              <div className="no-data">
                <p>No personnel assigned to this report.</p>
                {!readOnly && (role === 'admin' || role === 'read-write') && getAvailablePersonnel().length > 0 && (
                  <button onClick={() => setShowAssignmentForm(true)} className="btn btn-primary">
                    Assign First Personnel
                  </button>
                )}
              </div>
            )}
          </div>
        </>
      )}

      {/* No Report Selected */}
      {!reportId && activeTab === 'assignments' && (
        <div className="no-report">
          <p>Select a report to manage personnel assignments.</p>
          <button onClick={() => navigate('/daily-report')} className="btn btn-primary">
            Go to Daily Reports
          </button>
        </div>
      )}
    </div>
  );
}

// Export POB data for reports (read-only assignments view)
export const POBSection = ({ reportId, title = "Personnel On Board" }) => {
  return (
    <div className="report-section pob-section">
      <h3>{title}</h3>
      <POBManager reportId={reportId} readOnly={true} />
    </div>
  );
}; 